/**
 * Admin 开户审核队列 API（真联调 + mock 回退）
 * GET  /onboarding-applications?type=MERCHANT_ONBOARD|AGENT_ONBOARD
 * POST /onboarding-applications/{id}/review
 */
import { adminGet, adminPost, USE_MOCK } from './client';
import {
  onboardingApplications as mockOnboarding,
  type AuditStatus,
  type OnboardingType,
  type OrigOnboardingApplication,
  type ScreeningResult,
} from '@/mock/prdData';

// ---- 后端 DTO（Result.data） ----

/** 对齐 OnboardingQueueItemView */
type OnboardingQueueItemView = {
  applicationId: string;
  applicationNo?: string;
  type?: string;
  source?: string;
  status: string;
  legalName?: string;
  subjectName?: string;
  registrationNo?: string;
  referrerAgentId?: string;
  agentName?: string;
  screeningTags?: string[];
  screeningResult?: string;
  submittedAt?: string;
  claimerId?: string;
  waitingSeconds?: number;
  version?: number;
  /** 资质变更 Diff（仅 PROFILE_CHANGE；后端列表不返回，前端 mock 使用） */
  kycDiff?: { field: string; fieldLabel: string; oldValue: string; newValue: string }[];
};

/** 对齐 OnboardingListResult */
type OnboardingListResult = {
  total: number;
  page: number;
  pageSize: number;
  items: OnboardingQueueItemView[];
};

/** 对齐 OnboardingScreeningView（API-ONB-006 screening） */
type OnboardingScreeningView = {
  hitTypes: string[];
  autoRejected: boolean;
  publicReason?: string | null;
  systemReasonCode?: string | null;
};

/** 对齐 OnboardingDocumentSummary（API-ONB-006 documents） */
export type OnboardingDocumentSummary = {
  docType: string;
  fileId?: string | null;
  label: string;
};

/** 对齐 ProfileChangeDiffView（资质变更 Diff；非变更类为空数组） */
export type ProfileChangeDiffView = {
  fieldPath: string;
  oldValue: string;
  newValue: string;
};

/** 对齐 OnboardingDetailView（API-ONB-006 审核工作台详情） */
export type OnboardingDetailView = {
  applicationId: string;
  applicationNo?: string;
  type?: string;
  source?: string;
  status: string;
  legalName?: string;
  subjectName?: string;
  registrationNo?: string;
  referrerAgentId?: string;
  agentName?: string;
  screeningTags?: string[];
  screeningResult?: string;
  submittedAt?: string;
  claimerId?: string;
  waitingSeconds?: number;
  version?: number;
  screening?: OnboardingScreeningView;
  internalRemarks?: string;
  documents?: OnboardingDocumentSummary[];
  resubmitFields?: string[];
  publicRejectReason?: string | null;
  systemReasonCode?: string | null;
  createdAt?: string;
  updatedAt?: string;
  diff?: ProfileChangeDiffView[];
  /** 附录 A：PERSONAL | CORP（与后端 OnboardingDetailView 末尾字段同名） */
  agentType?: string | null;
  /** 附录 A：法人/负责人姓名 */
  legalPersonName?: string | null;
  /** 附录 A：证件类型（ID_CARD / PASSPORT / HKID / OTHER） */
  legalPersonIdType?: string | null;
  /** 附录 A：证件号掩码（仅此字段；禁止回显 legalPersonIdNo / legalPersonIdEnc） */
  legalPersonIdNoMasked?: string | null;
  /** 附录 A：证件有效期（ISO date） */
  legalPersonIdExpiry?: string | null;
  /** 附录 A：业务场景 */
  businessScene?: string | null;
  /** 附录 A：预估月交易量（string；非 GMV） */
  estimatedVolume?: string | null;
  /** 附录 A：执照/证件对象键 */
  licenseFileId?: string | null;
  /** 登录邮箱（EMAIL-REG） */
  loginEmail?: string | null;
  /** 审核通过开通后的主体 ID（与建号 subjectId 同一 Agent） */
  agentId?: string;
  /** 初定等级码（与 initial_tier 回显口径一致） */
  tier?: string;
  /** 开通账号邮箱是否已验证；首次开通为 false */
  emailVerified?: boolean;
  // S1 密码明文收口：审核响应不再返回初始密码字段，初始密码仅邮件送达
};

type OnboardingReviewDecision = 'APPROVE' | 'REJECT' | 'RESUBMIT';

type OnboardingReviewBody = {
  /** 审核层级；可选，缺省 REVIEW（单级审批） */
  stage?: string;
  decision: OnboardingReviewDecision;
  reason?: string;
  version?: number;
  resubmitFields?: string[];
  internalRemark?: string;
  /** 代理开户 APPROVE 初定等级（S/A/B/C/D），对齐 OnboardingReviewRequest.initialTier */
  initialTier?: string;
  /** US-804 强制清单 ID，对齐 OnboardingReviewRequest.checklistIds */
  checklistIds?: string[];
};

/** UI 行：在 Orig 上扩展乐观锁与原始状态 */
export type UiOnboardingApplication = OrigOnboardingApplication & {
  version?: number;
  rawStatus?: string;
};

// ---- UI 映射 ----

export function mapAuditStatus(status?: string): AuditStatus {
  const u = (status || '').toUpperCase();
  // 草稿（两段式进件第一态）不可审，不得兜底落入 PENDING 计入待审数
  if (u === 'DRAFT') return 'DRAFT';
  if (u === 'APPROVED') return 'APPROVED';
  if (u.includes('REJECT')) return 'REJECTED';
  if (u.includes('RESUBMIT') || u.includes('SUPPLEMENT')) return 'SUPPLEMENT';
  return 'PENDING';
}

function mapScreeningResult(result?: string): ScreeningResult {
  const u = (result || '').toUpperCase();
  if (u === 'PASS') return 'PASS';
  if (u === 'HIT') return 'HIT';
  return 'PENDING';
}

function mapOnboardingType(type?: string): OnboardingType {
  const u = (type || '').toUpperCase();
  if (u === 'AGENT_ONBOARD') return 'AGENT_ONBOARD';
  if (u === 'MERCHANT_PROFILE_CHANGE' || u === 'PROFILE_CHANGE') return 'MERCHANT_PROFILE_CHANGE';
  return 'MERCHANT_ONBOARD';
}

// 时间展示统一：api 层不再预格式化 submittedAt，保留原始 ISO，展示层用 formatDateTime

/** UI decision SUPPLEMENT → API RESUBMIT */
export function toApiDecision(
  decision: 'APPROVE' | 'REJECT' | 'SUPPLEMENT',
): OnboardingReviewDecision {
  return decision === 'SUPPLEMENT' ? 'RESUBMIT' : decision;
}

export function toOrigOnboarding(item: OnboardingQueueItemView): UiOnboardingApplication {
  const subjectName = item.subjectName || item.legalName || '—';
  return {
    applicationId: item.applicationId,
    applicationNo: item.applicationNo,
    subjectName,
    type: mapOnboardingType(item.type),
    // 原始 ISO 透传，展示层 formatDateTime 兜底空值
    submittedAt: item.submittedAt ?? '',
    screeningResult: mapScreeningResult(item.screeningResult),
    status: mapAuditStatus(item.status),
    agentName: item.agentName,
    source: item.source,
    registrationNo: item.registrationNo,
    screeningTags: item.screeningTags,
    claimerId: item.claimerId,
    waitingSeconds: item.waitingSeconds,
    kycDiff: item.kycDiff,
    submitter: '—',
    version: item.version,
    rawStatus: item.status,
  };
}

// ---- API ----

/**
 * 商户开户审核队列。
 * Query：type=MERCHANT_ONBOARD（别名 application_type 亦可）。
 * W4-TD3 直查：merchantId 非空时以蛇形 merchant_id 精确直查（对齐同端点 agent_id/page_size 风格，
 * 按 onboarding_application.subject_id 精确关联）；此时不传 keyword（合同 §4.3：merchant_id 优先）。
 * 直查语义：items 按 updatedAt 倒序，items[0] 即该商户最新一条申请。
 */
export async function listMerchantOnboardingApplications(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
  keyword?: string;
  /** 商户 id 直查（W4-TD3 新增）；缺省不传 = 既有 keyword 队列查询，行为零变化 */
  merchantId?: number | string;
}) {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;
  const qs = new URLSearchParams({
    type: 'MERCHANT_ONBOARD',
    page: String(page),
    page_size: String(pageSize),
  });
  if (params?.status) qs.set('status', params.status);
  const merchantId = params?.merchantId;
  const hasMerchantId =
    merchantId !== undefined && merchantId !== null && String(merchantId).trim() !== '';
  if (hasMerchantId) {
    // 直查：merchant_id 优先，keyword 不再传（避免启发式歧义）
    qs.set('merchant_id', String(merchantId));
  } else if (params?.keyword) {
    qs.set('keyword', params.keyword);
  }

  return adminGet<OnboardingListResult>(`/onboarding-applications?${qs.toString()}`, () => {
    const filtered = mockOnboarding.filter((a) => a.type === 'MERCHANT_ONBOARD');
    // W4-TD3 mock 同步：直查时仅返回绑定该商户（subjectId 匹配）的申请，无绑定 → 空列表（非报错）
    const scoped = hasMerchantId
      ? filtered.filter(
          (a) => a.subjectId !== undefined && String(a.subjectId) === String(merchantId),
        )
      : filtered;
    const items: OnboardingQueueItemView[] = scoped.map((a) => ({
      applicationId: a.applicationId,
      applicationNo: a.applicationNo || a.applicationId,
      type: a.type,
      source: a.source,
      status:
        a.status === 'PENDING'
          ? 'PENDING_APPROVAL'
          : a.status === 'SUPPLEMENT'
            ? 'PENDING_RESUBMIT'
            : a.status,
      legalName: a.subjectName,
      subjectName: a.subjectName,
      agentName: a.agentName,
      registrationNo: a.registrationNo,
      screeningTags: a.screeningTags,
      screeningResult: a.screeningResult,
      submittedAt: a.submittedAt,
      claimerId: a.claimerId,
      waitingSeconds: a.waitingSeconds ?? 0,
      version: 0,
    }));
    return { total: items.length, page, pageSize, items };
  });
}

/**
 * 商户开户审核队列。
 * Query：type=MERCHANT_ONBOARD（别名 application_type 亦可）。
 */
export async function listAgentOnboardingApplications(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
  keyword?: string;
}) {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;
  const qs = new URLSearchParams({
    type: 'AGENT_ONBOARD',
    page: String(page),
    page_size: String(pageSize),
  });
  if (params?.status) qs.set('status', params.status);
  if (params?.keyword) qs.set('keyword', params.keyword);

  return adminGet<OnboardingListResult>(`/onboarding-applications?${qs.toString()}`, () => {
    const filtered = mockOnboarding.filter((a) => a.type === 'AGENT_ONBOARD');
    const items: OnboardingQueueItemView[] = filtered.map((a) => ({
      applicationId: a.applicationId,
      applicationNo: a.applicationNo || a.applicationId,
      type: a.type,
      source: a.source,
      status:
        a.status === 'PENDING'
          ? 'PENDING_APPROVAL'
          : a.status === 'SUPPLEMENT'
            ? 'PENDING_RESUBMIT'
            : a.status,
      legalName: a.subjectName,
      subjectName: a.subjectName,
      agentName: a.agentName,
      registrationNo: a.registrationNo,
      screeningTags: a.screeningTags,
      screeningResult: a.screeningResult,
      submittedAt: a.submittedAt,
      claimerId: a.claimerId,
      waitingSeconds: a.waitingSeconds ?? 0,
      version: 0,
    }));
    return { total: items.length, page, pageSize, items };
  });
}

/**
 * 商户资质变更审核队列（PAGE-PLT-ACC-003）。
 * Query：type=MERCHANT_PROFILE_CHANGE（后端 ApplicationType 真值）。
 */
export async function listProfileChangeApplications(params?: {
  page?: number;
  pageSize?: number;
  status?: string;
  keyword?: string;
}) {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 50;
  const qs = new URLSearchParams({
    type: 'MERCHANT_PROFILE_CHANGE',
    page: String(page),
    page_size: String(pageSize),
  });
  if (params?.status) qs.set('status', params.status);
  if (params?.keyword) qs.set('keyword', params.keyword);

  return adminGet<OnboardingListResult>(`/onboarding-applications?${qs.toString()}`, () => {
    const filtered = mockOnboarding.filter((a) => a.type === 'MERCHANT_PROFILE_CHANGE');
    const items: OnboardingQueueItemView[] = filtered.map((a) => ({
      applicationId: a.applicationId,
      applicationNo: a.applicationNo || a.applicationId,
      type: a.type,
      source: a.source,
      status:
        a.status === 'PENDING'
          ? 'PENDING_APPROVAL'
          : a.status === 'SUPPLEMENT'
            ? 'PENDING_RESUBMIT'
            : a.status,
      legalName: a.subjectName,
      subjectName: a.subjectName,
      agentName: a.agentName,
      registrationNo: a.registrationNo,
      screeningTags: a.screeningTags,
      screeningResult: a.screeningResult,
      submittedAt: a.submittedAt,
      claimerId: a.claimerId,
      waitingSeconds: a.waitingSeconds ?? 0,
      kycDiff: a.kycDiff,
      version: 0,
    }));
    return { total: items.length, page, pageSize, items };
  });
}

/**
 * 提交审核决策。
 * Header Idempotency-Key 由 adminPost 自动注入。
 * 敏感写：强制 X-OTP。
 */
export async function reviewOnboardingApplication(
  applicationId: string,
  body: OnboardingReviewBody,
  otp: string,
) {
  return adminPost<OnboardingDetailView>(
    `/onboarding-applications/${applicationId}/review`,
    body,
    () => ({
      applicationId,
      status:
        body.decision === 'APPROVE'
          ? 'APPROVED'
          : body.decision === 'REJECT'
            ? 'REJECTED'
            : 'PENDING_RESUBMIT',
    }),
    { headers: { 'X-OTP': otp } },
  );
}

// ---- 详情（API-ONB-006）：对齐 OnboardingDetailView ----

/** mock 详情：从行数据推导，补充 screening/documents */
function mockDetailFor(item: OrigOnboardingApplication): OnboardingDetailView {
  const hitTypes = item.screeningTags?.filter((t) => t !== 'NONE') ?? [];
  const isHit = item.screeningResult === 'HIT';
  return {
    applicationId: item.applicationId,
    applicationNo: item.applicationNo || item.applicationId,
    type: item.type,
    source: item.source,
    status:
      item.status === 'PENDING'
        ? 'PENDING_APPROVAL'
        : item.status === 'SUPPLEMENT'
          ? 'PENDING_RESUBMIT'
          : item.status,
    legalName: item.subjectName,
    subjectName: item.subjectName,
    registrationNo: item.registrationNo,
    referrerAgentId: undefined,
    agentName: item.agentName,
    screeningTags: hitTypes,
    screeningResult: item.screeningResult,
    submittedAt: item.submittedAt,
    claimerId: item.claimerId,
    waitingSeconds: item.waitingSeconds ?? 0,
    version: 0,
    screening: {
      hitTypes,
      autoRejected: isHit,
      publicReason: isHit ? item.comment : null,
      systemReasonCode: isHit ? 'SCREENING_HIT' : null,
    },
    internalRemarks: '',
    documents: [
      { docType: 'LICENSE', fileId: 'admin/agent-onboarding/mock-license', label: '营业执照' },
      { docType: 'LEGAL_PERSON_ID', fileId: 'admin/agent-onboarding/mock-legal-id', label: '法人证件' },
    ],
    resubmitFields: item.status === 'SUPPLEMENT' ? ['license_file_id'] : [],
    publicRejectReason: item.status === 'REJECTED' ? item.comment ?? null : null,
    systemReasonCode: isHit ? 'SCREENING_HIT' : null,
    createdAt: item.submittedAt,
    updatedAt: item.reviewedAt ?? item.submittedAt,
    diff:
      item.type === 'MERCHANT_PROFILE_CHANGE' && item.kycDiff
        ? item.kycDiff.map((d) => ({
            fieldPath: d.field,
            oldValue: d.oldValue,
            newValue: d.newValue,
          }))
        : [],
    agentType: 'CORP',
    legalPersonName: item.subjectName,
    legalPersonIdType: 'ID_CARD',
    legalPersonIdNoMasked: '**************1234',
    legalPersonIdExpiry: '2030-12-31',
    businessScene: '跨境收单代理（mock）',
    estimatedVolume: '100000.00',
    licenseFileId: 'admin/agent-onboarding/mock-license',
    loginEmail: 'login@example.com',
  };
}

/**
 * 申请详情（API-ONB-006）：审核工作台展示完整资质/筛查/证照/轨迹。
 * GET /admin/api/v1/onboarding-applications/{applicationId}
 */
export async function getOnboardingApplicationDetail(applicationId: string) {
  return adminGet<OnboardingDetailView>(
    `/onboarding-applications/${encodeURIComponent(applicationId)}`,
    () => {
      const item = mockOnboarding.find((a) => a.applicationId === applicationId);
      if (!item) {
        throw new Error(`申请 ${applicationId} 不存在`);
      }
      return mockDetailFor(item);
    },
  );
}

export { USE_MOCK };
